import {createPortError,isPortError} from '../platform/port-error.js';
import {requireContextProvider,requireEvidenceSource,requireSafetyKnowledgeProvider,requireAssistantProvider,requireAssistantPolicy,createAssistantConsent,createEvidenceReference} from './assistant-contracts.js';

function validated(entries){
  if(!Array.isArray(entries))throw new TypeError('Safety knowledge must be an array');
  // Approval metadata is source-owned; provider output cannot grant validation.
  return entries.filter(entry=>entry?.status==='validated'&&!entry.retiredAt&&entry.reviewedBy&&entry.reviewedAt&&!Number.isNaN(Date.parse(entry.reviewedAt))&&Number.isInteger(entry.version)&&entry.version>0&&entry.id&&entry.source?.reference);
}
function knowledgeRefs(entries){
  return entries.map(entry=>createEvidenceReference({kind:'safety_knowledge',ref:String(entry.id)+'@'+entry.version}));
}
export function createCraftAssistant({
  contextProvider,evidenceSource,safetyKnowledge=null,assistantProvider=null,policy,
  timeoutMs=20_000,errorCodes={}
}={}){
  const contextPort=requireContextProvider(contextProvider),evidencePort=requireEvidenceSource(evidenceSource);
  const knowledgePort=requireSafetyKnowledgeProvider(safetyKnowledge,{optional:true});
  const providerPort=requireAssistantProvider(assistantProvider,{optional:true}),rules=requireAssistantPolicy(policy);
  const codes={cancelled:'ASSISTANT_CANCELLED',timeout:'ASSISTANT_TIMEOUT',unavailable:'ASSISTANT_PROVIDER_UNAVAILABLE',privateText:'ASSISTANT_PRIVATE_TEXT_CONSENT_REQUIRED',...errorCodes};
  const timeout=Number(timeoutMs);
  if(!Number.isFinite(timeout)||timeout<10||timeout>60_000)throw new TypeError('timeoutMs must be between 10 and 60000');
  function assertActive(signal){if(signal?.aborted)throw createPortError(codes.cancelled,{cancelled:true});}
  async function callProvider(envelope,signal){
    assertActive(signal);
    const controller=new AbortController();
    let timer,onAbort;
    try{
      const boundary=new Promise((resolve,reject)=>{
        onAbort=()=>{controller.abort();reject(createPortError(codes.cancelled,{cancelled:true}));};
        signal?.addEventListener?.('abort',onAbort,{once:true});
        timer=setTimeout(()=>{controller.abort();reject(createPortError(codes.timeout,{timeout:true,retryable:true}));},timeout);
      });
      const result=await Promise.race([Promise.resolve().then(()=>providerPort.respond(envelope,{signal:controller.signal})),boundary]);
      assertActive(signal);return result;
    }catch(error){
      if(isPortError(error))throw createPortError(error.code,{retryable:error.retryable,timeout:error.timeout,cancelled:error.cancelled});
      if(signal?.aborted||error?.name==='AbortError')throw createPortError(codes.cancelled,{cancelled:true});
      throw createPortError(codes.unavailable,{retryable:true});
    }finally{
      clearTimeout(timer);signal?.removeEventListener?.('abort',onAbort);
    }
  }
  async function respond({prompt,surfaceContext={surface:'home'},selectedHistoryIds=[],privateTextNeeded=false,privateTextConsent=false,providerProcessingConsent=false,signal}={}){
    assertActive(signal);
    const userPrompt=String(prompt??'').trim().slice(0,4000);
    if(!userPrompt)throw new TypeError('prompt is required');
    const consent=createAssistantConsent({privateTextNeeded,privateTextConsent,providerProcessingConsent},{privateTextErrorCode:codes.privateText});
    const resolved=await contextPort.resolve({surfaceContext,request:{privateTextNeeded:consent.privateTextIncluded},signal})||{};
    assertActive(signal);
    const context=resolved.context||await contextPort.assemble(resolved,{privateTextNeeded:consent.privateTextIncluded});
    if(context?.privacy?.privateTextIncluded===true&&!consent.privateTextIncluded)throw new Error(codes.privateText);
    const allowedRefs=(await evidencePort.references(resolved)||[]).map(createEvidenceReference);
    const attach=value=>Object.freeze({...value,context,surfaceContext});
    const route=rules.route(userPrompt,resolved);
    if(route==='safety'){
      const entries=validated(await knowledgePort?.retrieve({topics:rules.topics(userPrompt),signal})||[]);
      assertActive(signal);
      if(!entries.length)return attach(rules.fallback('validated_knowledge_unavailable'));
      return attach(rules.validate(rules.safetyResponse(entries),{allowedEvidenceRefs:allowedRefs,validatedKnowledgeRefs:knowledgeRefs(entries)}));
    }
    if(route!=='provider'){
      const local=await rules.localResponse(route,{resolved,prompt:userPrompt,selectedHistoryIds,consent});
      assertActive(signal);
      if(local?.status==='needs_history_selection')return attach(local);
      return attach(rules.validate(local,{allowedEvidenceRefs:allowedRefs}));
    }
    if(!providerPort)return attach({...rules.providerUnavailable(),providerUnavailable:true});
    if(consent.providerProcessingConsent!==true)return attach({...rules.consentRequired(),providerConsentRequired:true});
    const envelope=rules.envelope({prompt:userPrompt,context,surface:surfaceContext?.surface||'home',privateTextIncluded:context?.privacy?.privateTextIncluded===true,providerProcessingConsent:true});
    const raw=await callProvider(envelope,signal);
    const entries=validated(await knowledgePort?.retrieve({topics:[],signal})||[]);
    assertActive(signal);
    return attach({...rules.validate(raw,{allowedEvidenceRefs:allowedRefs,validatedKnowledgeRefs:knowledgeRefs(entries)}),providerUsed:true});
  }
  return Object.freeze({respond});
}
